import type { RecognizedLabelData, ScanCaptureDetails, ScanOcrDetails, ScanOcrFieldResult } from "../types/scanWorkflow";
import { applyConfidenceCalibration, CONFIDENCE_CALIBRATION_CONTRACT_ID, type ConfidenceCalibrationArtifact, type ConfidenceRuntimeFeatures } from "./confidenceCalibrationService";
import { readCachedActiveVisualLabProfile } from "./visualLabService";

export const STEP2_UNCERTAINTY_UX_CONTRACT_ID = "step2-uncertainty-ux-v1-sr37" as const;
export type Step2UncertaintyField = "materialText" | "materialType" | "materialNumber" | "storageAddress";
export type Step2UncertaintyStatus = "ok" | "uncertain" | "missing" | "technical_error" | "unclassified";

export interface Step2UncertaintyFieldState {
  field: Step2UncertaintyField;
  value: string;
  status: Step2UncertaintyStatus;
  probability: number | null;
  threshold: number | null;
  evidenceScore: number | null;
  featuresUsed: string[];
  message: string | null;
}

export interface Step2UncertaintyFinalization {
  schemaVersion: 1;
  contractId: typeof STEP2_UNCERTAINTY_UX_CONTRACT_ID;
  finalizedAt: string;
  finalizationBeforeNavigation: true;
  source: "sr33-production-calibration" | "no-compatible-calibration" | "technical-error";
  productionBundleId: string | null;
  confidenceArtifactSha256: string | null;
  materialMasterUsed: false;
  geometryUsable: boolean;
  retakeRecommended: boolean;
  fields: Record<Step2UncertaintyField, Step2UncertaintyFieldState>;
  fourOfFour: { probability: number | null; threshold: number | null; uncertain: boolean | null };
}

export interface Step2FieldUiState {
  field: Step2UncertaintyField;
  severity: "warning" | "error" | null;
  message: string | null;
  shouldMarkCorrected: boolean;
}

const FIELD_BY_LABEL: Record<string, Step2UncertaintyField> = {
  Materialnummer: "materialNumber",
  Bezeichnung: "materialText",
  Typ: "materialType",
  "Lagerort (Gebäude Lagerort Feld Fach)": "storageAddress",
};
const FIELD_ORDER: Step2UncertaintyField[] = ["materialText", "materialType", "materialNumber", "storageAddress"];
const UI_TTL_MS = 30 * 60 * 1000;
let activeFinalization: Step2UncertaintyFinalization | null = null;
const correctedFields = new Set<Step2UncertaintyField>();

function clean(value: unknown): string {
  return String(value ?? "").replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();
}
function clamp01(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  if (parsed > 1) return Math.max(0, Math.min(1, parsed / 100));
  return Math.max(0, Math.min(1, parsed));
}
function quality01(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed <= 1 ? parsed : parsed / (parsed + 100);
}
function fieldResult(details: ScanOcrDetails, field: Step2UncertaintyField): ScanOcrFieldResult | null {
  return details.fields.find((entry) => entry.field === field) ?? null;
}
function grammarPlausibility(field: Step2UncertaintyField, value: string): number {
  const text = clean(value);
  if (!text) return 0;
  if (field === "materialNumber") return /^983\d{7}$/.test(text.replace(/\s/g, "")) ? 1 : 0;
  if (field === "storageAddress") return /^\d{2}[A-Z]\s+\d{3}\s+\d{2}\s+\d{2}$/i.test(text) ? 1 : 0.35;
  if (field === "materialType") return text.length >= 2 ? 1 : 0.4;
  return text.length >= 3 ? 1 : 0.5;
}
function passAgreement(result: ScanOcrFieldResult | null): number | null {
  if (!result) return null;
  const selected = clean(result.text).toLocaleUpperCase("de-DE");
  const passes = Array.isArray(result.passes) ? result.passes : [];
  if (!passes.length) return result.passCount && result.passCount > 1 ? null : 1;
  const usable = passes.map((entry) => clean(entry.text).toLocaleUpperCase("de-DE")).filter(Boolean);
  if (!usable.length) return selected ? 0 : null;
  return usable.filter((value) => value === selected).length / usable.length;
}
function add(features: ConfidenceRuntimeFeatures, key: keyof ConfidenceRuntimeFeatures, value: number | null | undefined) {
  if (value != null && Number.isFinite(value)) features[key] = Math.max(0, Math.min(1, value));
}
function featureSet(field: Step2UncertaintyField, labelData: RecognizedLabelData, ocr: ScanOcrDetails, scan: ScanCaptureDetails | null): ConfidenceRuntimeFeatures {
  const result = fieldResult(ocr, field);
  const features: ConfidenceRuntimeFeatures = {};
  add(features, "ocr_confidence", clamp01(result?.confidence));
  const agreement = passAgreement(result);
  add(features, "pass_agreement", agreement);
  add(features, "variant_stability", agreement);
  add(features, "grammar_plausibility", grammarPlausibility(field, labelData[field]));
  add(features, "geometry_confidence", clamp01(scan?.mapping?.confidence ?? scan?.detection?.score));
  add(features, "template_quality", clamp01(scan?.detection?.finalGeometry?.averageSupport));
  add(features, "sharpness_quality", quality01(scan?.quality?.sharpness));
  add(features, "contrast_quality", quality01(scan?.quality?.contrast));
  add(features, "cnn_confidence", clamp01(scan?.detection?.visionSnapshot?.cnnInference?.confidence));
  return features;
}
function globalFeatures(fields: Record<Step2UncertaintyField, ConfidenceRuntimeFeatures>): ConfidenceRuntimeFeatures {
  const out: ConfidenceRuntimeFeatures = {};
  const keys = new Set(Object.values(fields).flatMap((features) => Object.keys(features)));
  for (const key of keys) {
    const values = FIELD_ORDER.map((field) => Number(fields[field][key as keyof ConfidenceRuntimeFeatures])).filter(Number.isFinite);
    if (values.length) out[key as keyof ConfidenceRuntimeFeatures] = Math.min(...values);
  }
  return out;
}
function confidenceArtifact(): { artifact: ConfidenceCalibrationArtifact | null; bundleId: string | null } {
  const bundle = readCachedActiveVisualLabProfile()?.production_bundle ?? null;
  const raw = bundle?.candidate_config?.confidence;
  if (!raw || typeof raw !== "object") return { artifact: null, bundleId: bundle?.bundle_id ?? null };
  const artifact = raw as Partial<ConfidenceCalibrationArtifact>;
  if (artifact.contract_id !== CONFIDENCE_CALIBRATION_CONTRACT_ID || artifact.threshold_policy !== "BENCHMARK_DERIVED" || artifact.production_activation_allowed !== false) {
    return { artifact: null, bundleId: bundle?.bundle_id ?? null };
  }
  return { artifact: raw as ConfidenceCalibrationArtifact, bundleId: bundle?.bundle_id ?? null };
}
function emptyField(field: Step2UncertaintyField, value: string, status: Step2UncertaintyStatus, message: string | null): Step2UncertaintyFieldState {
  return { field, value, status, probability: null, threshold: null, evidenceScore: null, featuresUsed: [], message };
}

export function finalizeStep2Uncertainty(
  labelData: RecognizedLabelData,
  ocrDetails: ScanOcrDetails,
  scanDetails: ScanCaptureDetails | null,
): Step2UncertaintyFinalization {
  const geometryUsable = Boolean(scanDetails?.detection?.found && scanDetails.detection.finalGeometryAccepted && scanDetails.rectification.used);
  const { artifact, bundleId } = confidenceArtifact();
  const values = Object.fromEntries(FIELD_ORDER.map((field) => [field, clean(labelData[field])])) as Record<Step2UncertaintyField, string>;
  let source: Step2UncertaintyFinalization["source"] = artifact ? "sr33-production-calibration" : "no-compatible-calibration";
  let artifactSha: string | null = artifact?.artifact_sha256 ?? null;
  let fourOfFour: Step2UncertaintyFinalization["fourOfFour"] = { probability: null, threshold: null, uncertain: null };
  let states = Object.fromEntries(FIELD_ORDER.map((field) => [field, emptyField(field, values[field], values[field] ? "unclassified" : "missing", values[field] ? null : "Kein nutzbarer Wert erkannt – bitte eingeben oder neu aufnehmen.")])) as Record<Step2UncertaintyField, Step2UncertaintyFieldState>;

  if (artifact && geometryUsable) {
    try {
      const allowed = new Set(artifact.feature_contract.allowed ?? []);
      const features = Object.fromEntries(FIELD_ORDER.map((field) => {
        const raw = featureSet(field, labelData, ocrDetails, scanDetails);
        const filtered = Object.fromEntries(Object.entries(raw).filter(([key]) => allowed.has(key as keyof ConfidenceRuntimeFeatures))) as ConfidenceRuntimeFeatures;
        return [field, filtered];
      })) as Record<Step2UncertaintyField, ConfidenceRuntimeFeatures>;
      const calibrated = applyConfidenceCalibration(artifact, features, globalFeatures(features));
      states = Object.fromEntries(FIELD_ORDER.map((field) => {
        const value = values[field];
        if (!value) return [field, emptyField(field, value, "missing", "Kein nutzbarer Wert erkannt – bitte eingeben oder neu aufnehmen.")];
        const result = calibrated.fields[field];
        return [field, {
          field,
          value,
          status: result.uncertain ? "uncertain" : "ok",
          probability: result.probability,
          threshold: result.threshold,
          evidenceScore: result.evidenceScore,
          featuresUsed: [...result.featuresUsed],
          message: result.uncertain ? "Unsicher erkannt – bitte prüfen" : null,
        } satisfies Step2UncertaintyFieldState];
      })) as Record<Step2UncertaintyField, Step2UncertaintyFieldState>;
      fourOfFour = { probability: calibrated.fourOfFour.probability, threshold: calibrated.fourOfFour.threshold, uncertain: calibrated.fourOfFour.uncertain };
    } catch {
      source = "technical-error";
      artifactSha = artifact.artifact_sha256 ?? null;
      states = Object.fromEntries(FIELD_ORDER.map((field) => [field, values[field]
        ? emptyField(field, values[field], "technical_error", "Unsicherheitsbewertung technisch nicht verfügbar – bitte Wert prüfen.")
        : emptyField(field, values[field], "missing", "Kein nutzbarer Wert erkannt – bitte eingeben oder neu aufnehmen.")])) as Record<Step2UncertaintyField, Step2UncertaintyFieldState>;
    }
  }

  const snapshot: Step2UncertaintyFinalization = {
    schemaVersion: 1,
    contractId: STEP2_UNCERTAINTY_UX_CONTRACT_ID,
    finalizedAt: new Date().toISOString(),
    finalizationBeforeNavigation: true,
    source,
    productionBundleId: bundleId,
    confidenceArtifactSha256: artifactSha,
    materialMasterUsed: false,
    geometryUsable,
    retakeRecommended: !geometryUsable,
    fields: states,
    fourOfFour,
  };
  activeFinalization = snapshot;
  correctedFields.clear();
  return snapshot;
}

export function getStep2FieldUiState(label: string, value: string): Step2FieldUiState | null {
  const field = FIELD_BY_LABEL[label];
  const snapshot = activeFinalization;
  if (!field || !snapshot) return null;
  const age = Date.now() - Date.parse(snapshot.finalizedAt);
  if (!Number.isFinite(age) || age < 0 || age > UI_TTL_MS) return null;
  if (correctedFields.has(field)) return { field, severity: null, message: null, shouldMarkCorrected: false };
  const baselineValue = clean(snapshot.fields[field].value);
  const currentValue = clean(value);
  if (currentValue !== baselineValue) return { field, severity: null, message: null, shouldMarkCorrected: true };
  const state = snapshot.fields[field];
  if (state.status === "uncertain") return { field, severity: "warning", message: state.message, shouldMarkCorrected: false };
  if (state.status === "missing" || state.status === "technical_error") return { field, severity: "error", message: state.message, shouldMarkCorrected: false };
  return { field, severity: null, message: null, shouldMarkCorrected: false };
}

export function markStep2FieldCorrected(field: Step2UncertaintyField): void {
  correctedFields.add(field);
}

export function resetStep2Uncertainty(): void {
  activeFinalization = null;
  correctedFields.clear();
}

export function getActiveStep2UncertaintyFinalization(): Step2UncertaintyFinalization | null {
  return activeFinalization ? structuredClone(activeFinalization) : null;
}
